/**
 * Location and Store Selector Component
 * Combines location detection with nearest store finding and selection
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { useUserLocation } from '../../hooks/useUserLocation';
import { useNearestStore } from '../../hooks/useNearestStore';
import LocationInput from './LocationInput';
import StoreSelector from './StoreSelector';

const LocationStoreSelector = ({
  onStoreSelected,
  onLocationUpdate,
  autoSelectNearest = true,
  showLocationInput = true,
  showStoreSelector = true,
  compact = false,
  collapsible = false,
  initialExpanded = true,
  enableConsoleLogging = false,
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [hasSelectedStore, setHasSelectedStore] = useState(false);

  // Location hook
  const {
    userLocation,
    isGettingLocation,
    locationError,
    getLocationWithFallbacks,
    setLocationFromAddress,
  } = useUserLocation({ 
    autoStart: true, 
    enableConsoleLogging 
  });

  // Nearest store hook
  const {
    stores,
    nearestStore,
    selectedStore,
    isLoadingStores,
    storeError,
    selectStore,
    refreshStores,
    getStoresByDistance,
  } = useNearestStore({
    userLocation,
    autoSelectNearest,
    enableConsoleLogging,
  });

  // Handle store selection
  const handleStoreSelect = (store) => {
    selectStore(store);
    setHasSelectedStore(true);
    onStoreSelected?.(store, userLocation);
  };

  // Handle location updates
  const handleLocationUpdate = (location) => {
    onLocationUpdate?.(location);
    // Reset store selection when location changes significantly
    if (hasSelectedStore && location) {
      setHasSelectedStore(false);
    }
  };

  // Auto-select nearest store when found
  useEffect(() => {
    if (nearestStore && !hasSelectedStore && autoSelectNearest) {
      selectStore(nearestStore);
      setHasSelectedStore(true);
      onStoreSelected?.(nearestStore, userLocation);
    }
  }, [nearestStore, hasSelectedStore, autoSelectNearest, selectStore, onStoreSelected, userLocation]);

  // Refresh both location and stores
  const handleRefresh = () => {
    getLocationWithFallbacks();
    refreshStores();
  };

  const content = (
    <Box>
      {/* Location Input Section */}
      {showLocationInput && (
        <Box sx={{ mb: showStoreSelector ? 2 : 0 }}>
          <LocationInput
            userLocation={userLocation}
            onLocationSelected={handleLocationUpdate}
            isLocationLoading={isGettingLocation}
            locationError={locationError}
            onGetLocation={getLocationWithFallbacks}
            onSetLocationFromAddress={setLocationFromAddress}
            compact={compact}
            placeholder="Enter your address to find nearby stores..."
          />
        </Box>
      )}

      {/* Store Selector Section */}
      {showStoreSelector && (
        <StoreSelector
          nearestStore={nearestStore}
          selectedStore={selectedStore}
          userLocation={userLocation}
          stores={getStoresByDistance()}
          isLoadingStores={isLoadingStores}
          storeError={storeError}
          onSelectStore={handleStoreSelect}
          onLocationUpdate={getLocationWithFallbacks}
          compact={compact}
          showChangeStoreButton={true}
          enableStoreSelection={true}
        />
      )}

      {/* Error States */}
      {locationError && !userLocation && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Location access denied. Please enter your address manually or enable location services.
          </Typography>
        </Alert>
      )}

      {/* No stores available */}
      {!isLoadingStores && stores.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            No stores available at the moment.
          </Typography>
          <Button size="small" onClick={refreshStores} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Debug Info (if logging enabled) */}
      {enableConsoleLogging && (userLocation || selectedStore) && (
        <Card sx={{ mt: 2, backgroundColor: 'grey.50' }}>
          <CardContent sx={{ py: 1 }}>
            <Typography variant="caption" display="block">
              Debug Info:
            </Typography>
            {userLocation && (
              <Typography variant="caption" display="block">
                Location: {userLocation.lat?.toFixed(6)}, {userLocation.lng?.toFixed(6)} 
                ({userLocation.method})
              </Typography>
            )}
            {selectedStore && (
              <Typography variant="caption" display="block">
                Selected Store: {selectedStore.businessName} 
                {selectedStore.distance && ` (${selectedStore.distance.toFixed(2)}km)`}
              </Typography>
            )}
            <Typography variant="caption" display="block">
              Available Stores: {stores.length}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  if (collapsible) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" component="h2">
              Location & Store Selection
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton size="small" onClick={handleRefresh} title="Refresh">
                <RefreshIcon />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => setExpanded(!expanded)}
                title={expanded ? "Collapse" : "Expand"}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {selectedStore && !expanded && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedStore.businessName}
              {selectedStore.distance && ` • ${selectedStore.distance.toFixed(1)}km away`}
            </Typography>
          )}
        </CardContent>

        <Collapse in={expanded}>
          <CardContent sx={{ pt: 0 }}>
            {content}
          </CardContent>
        </Collapse>
      </Card>
    );
  }

  return content;
};

export default LocationStoreSelector;
